import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Box } from '@material-ui/core';
import { TruncatedText } from '@/se/components/typography';
import { formatPhoneNumber, processPhoneNumbersString } from '@/se/components/inputs/PhoneInput';
import RouterLink from '@/se/components/RouterLink';
import Anesthesiologist from '../../../types/Anesthesiologist';
import { Block, Check } from '@material-ui/icons';
import { formatProviderId } from '@/util/providerId';
import { gql } from '@apollo/client';
import { PinColumn } from '../common/columns';
import { fragments } from '@/graph/anesthesiologists';
import StaffMember from '../../../types/StaffMember';
import TrackingColumn, { TrackingColumnProps } from '../../tracking/TrackingColumn';
import { useStaffCostAccess } from '@/hooks/useStaffCostAccess';
import MessageHistorySidePanel from '../common/MessageHistorySidePanel';

const Text = TruncatedText as any;

export const nameColumn = {
  title: 'Name',
  sortKey: 'name', // Backend mapping for server-side sort
  lens: (item: Anesthesiologist) => item.name,
  Component: (props: { data: string }) => <Text>{props.data}</Text>,
};

export const trackingColumn = {
  title: 'Location',
  lens: (data: StaffMember) => ({ bracelet: data.bracelet, room: data.room }),
  Component: ({
    data: { bracelet, room },
  }: {
    data: { bracelet: TrackingColumnProps['bracelet']; room: TrackingColumnProps['room'] };
  }) => <TrackingColumn bracelet={bracelet} room={room} />,
};

export const hstIdColumn = {
  title: 'Provider Id',
  sortKey: 'hstId', // Backend mapping for server-side sort
  lens: (item: Anesthesiologist) => item.hstId,
  Component: (props: { data: string }) => <Text>{formatProviderId(props.data)}</Text>,
};

export const phoneNumber = {
  title: 'Phone Number',
  lens: (item: Anesthesiologist) =>
    item.phoneNumber !== null && item.phoneNumber !== undefined
      ? formatPhoneNumber(processPhoneNumbersString(item.phoneNumber).nationalNumbers)
      : undefined,
  Component: (props: { data: string }) => <Text>{props.data || '-'}</Text>,
};

// Phone column with history icon for single entity views
interface PhoneWithHistoryData {
  formatted: string | undefined;
  raw: string | undefined;
  name: string;
}

const AnesthesiologistPhoneWithHistory = ({ data }: { data: PhoneWithHistoryData }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between">
    <Text>{data.formatted || '-'}</Text>
    {data.raw && <MessageHistorySidePanel phone={data.raw} label={`${data.name || 'Anesthesiologist'} SMS History`} />}
  </Box>
);

export const phoneNumberWithHistory = {
  title: 'Phone Number',
  lens: (item: Anesthesiologist) => ({
    formatted:
      item.phoneNumber !== null && item.phoneNumber !== undefined
        ? formatPhoneNumber(processPhoneNumbersString(item.phoneNumber).nationalNumbers)
        : undefined,
    raw: item.phoneNumber,
    name: item.name,
  }),
  Component: AnesthesiologistPhoneWithHistory,
};

export const scheduleAccessColumn = {
  title: 'Schedule Access',
  lens: (item: Anesthesiologist) => item,
  Component: withRouter((props: { data: Anesthesiologist } & RouteComponentProps) => (
    <RouterLink onClick={e => e.stopPropagation()} to={`${props.match.url}/schedule-access/${props.data.id}`}>
      {props.data.hasScheduleAccess ? <Check fontSize={'small'} /> : <Block fontSize={'small'} />}
      <Text>{props.data.hasScheduleAccess ? 'Yes' : 'No'}</Text>
    </RouterLink>
  )),
};

export const emailColumn = {
  title: 'Email',
  sortKey: 'email', // Backend mapping for server-side sort
  lens: (item: Anesthesiologist) => item,
  Component: (props: { data: Anesthesiologist }) => <Text>{props.data?.email}</Text>,
};

export const sendNotificationColumn = {
  title: 'Notify',
  lens: (item: Anesthesiologist) => item,
  Component: (props: { data: Anesthesiologist }) => <Text>{props.data.shouldSendNotification ? 'Yes' : 'No'}</Text>,
};

export const isCrnaColumn = {
  title: 'CRNA',
  lens: (item: Anesthesiologist) => item,
  Component: (props: { data: Anesthesiologist }) => <Text>{props.data.isCrna ? 'Yes' : 'No'}</Text>,
};

export const hourlyRateColumn = {
  title: 'Hourly Rate',
  lens: (data: Anesthesiologist) => {
    const hasAccess = useStaffCostAccess();
    return hasAccess ? data.hourlyRate : null;
  },
  Component: (props: { data: number | null }) => <Text>{props.data === null ? '-' : `$${props.data}`}</Text>,
};

export const pinColumn = PinColumn<Anesthesiologist & { pin?: string }>({
  getId: data => data.id,
  getName: data => data.name,
  getPin: data => data.pin,
  createPinMutation: gql`
    mutation anesthesiologistCreatePin($id: Long!, $pin: String!) {
      anesthesiologistSetPin(id: $id, pin: $pin) {
        ...AnesthesiologistData
        pin
      }
    }
    ${fragments.all}
  `,
  issuePinMutation: gql`
    mutation anesthesiologistIssuePin($id: Long!) {
      anesthesiologistIssuePin(id: $id) {
        ...AnesthesiologistData
        pin
      }
    }
    ${fragments.all}
  `,
  removePinMutation: gql`
    mutation anesthesiologistRemovePin($id: Long!) {
      anesthesiologistRemovePin(id: $id) {
        ...AnesthesiologistData
        pin
      }
    }
    ${fragments.all}
  `,
});

export const baseColumns = [
  nameColumn,
  hstIdColumn,
  phoneNumber,
  emailColumn,
  scheduleAccessColumn,
  sendNotificationColumn,
  isCrnaColumn,
  pinColumn,
  trackingColumn,
];
export const viewColumns = [
  hstIdColumn,
  phoneNumberWithHistory,
  emailColumn,
  sendNotificationColumn,
  isCrnaColumn,
  hourlyRateColumn,
  trackingColumn,
];
